import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Mail, Sparkles } from 'lucide-react';
import {
  BoutonMint,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampRecherche } from '../components/ui/Onglets';
import { useToast } from '../components/ui/useToast';
import { ModaleDiffusionOffres } from '../components/sans-entreprise/ModaleDiffusionOffres';
import { ModaleTransmission } from '../components/sans-entreprise/ModaleTransmission';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useDefinirMobilite,
  useDefinirRecherche,
  useSansContrat,
  type CodeAnciennete,
  type CodeTrancheAge,
  type EtatRecherche,
  type FiltreOffres,
  type FiltresSansContrat,
  type LigneSansContrat,
  type Mobilite,
  type SituationRecherche,
} from '../lib/usePedagogique';
import { ErreurApi } from '../lib/api';
import { useAuth } from '../auth/useAuth';

function dateCourte(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * Écran « Apprenants sans entreprise » (CDC §6.9).
 *
 * Les personnes inscrites en formation sans entreprise, ouvert sur celles qui
 * sont en recherche active, filtrables pour cibler la diffusion d'offres. Qui
 * figure ici, dans quelle situation et ce qu'elle a déjà reçu : tout vient de
 * l'API (`domain/sans-contrat.ts`), filtres compris. L'écran ne garde que la
 * sélection, et la purge de ce que les filtres ont fait sortir.
 */
export function PedSansContratPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { site } = usePerimetre();
  const { utilisateur, peutAcceder } = useAuth();
  const vuePlacement = utilisateur?.vueCandidat === 'PLACEMENT';
  const aLeMatching = peutAcceder('com-matching');
  // La relation entreprise et le recrutement sont le commercial : se
  // transmettre la sélection à soi-même n'aurait pas de sens.
  const estCommercial =
    utilisateur?.profil === 'RELATION_ENTREPRISE' || utilisateur?.profil === 'RECRUTEMENT';

  const [etat, setEtat] = useState<EtatRecherche>('ACTIVE');
  const [sexe, setSexe] = useState<'F' | 'M' | ''>('');
  const [trancheAge, setTrancheAge] = useState<CodeTrancheAge | ''>('');
  const [mineur, setMineur] = useState(false);
  const [rqth, setRqth] = useState(false);
  const [formation, setFormation] = useState('');
  const [commune, setCommune] = useState('');
  const [mobilite, setMobilite] = useState<Mobilite | ''>('');
  const [situation, setSituation] = useState<SituationRecherche | ''>('');
  const [anciennete, setAnciennete] = useState<CodeAnciennete | ''>('');
  const [offres, setOffres] = useState<FiltreOffres | ''>('');
  const [recherche, setRecherche] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const [diffusionOuverte, setDiffusionOuverte] = useState(false);
  const [transmissionOuverte, setTransmissionOuverte] = useState(false);

  const filtres: FiltresSansContrat = {
    site,
    etat,
    ...(sexe ? { sexe } : {}),
    ...(trancheAge ? { trancheAge } : {}),
    ...(mineur ? { mineur } : {}),
    ...(rqth ? { rqth } : {}),
    ...(formation ? { formation } : {}),
    ...(commune ? { commune } : {}),
    ...(mobilite ? { mobilite } : {}),
    ...(situation ? { situation } : {}),
    ...(anciennete ? { anciennete } : {}),
    ...(offres ? { offres } : {}),
    ...(recherche.trim() ? { recherche: recherche.trim() } : {}),
  };

  const { data, isLoading, error } = useSansContrat(filtres);
  const definirMobilite = useDefinirMobilite();
  const definirRecherche = useDefinirRecherche();

  const compteurs = data?.compteurs;
  const lignes = useMemo(() => data?.apprenants ?? [], [data?.apprenants]);

  // Une ligne que les filtres ont fait sortir ne reste pas sélectionnée : on
  // diffuserait à des personnes qu'on ne voit plus.
  useEffect(() => {
    const visibles = new Set(lignes.map((ligne) => ligne.apprenantId));
    setSelection((liste) => {
      const gardee = liste.filter((id) => visibles.has(id));
      return gardee.length === liste.length ? liste : gardee;
    });
  }, [lignes]);

  const tousSelectionnes = lignes.length > 0 && selection.length === lignes.length;

  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [
    site,
    etat,
    sexe,
    trancheAge,
    mineur,
    rqth,
    formation,
    commune,
    mobilite,
    situation,
    anciennete,
    offres,
    recherche,
    taillePage,
  ]);

  const total = lignes.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const lignesAffichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return lignes.slice(debut, debut + taillePage);
  }, [lignes, page, taillePage]);

  function reinitialiser(): void {
    setEtat('ACTIVE');
    setSexe('');
    setTrancheAge('');
    setMineur(false);
    setRqth(false);
    setFormation('');
    setCommune('');
    setMobilite('');
    setSituation('');
    setAnciennete('');
    setOffres('');
    setRecherche('');
  }

  function basculerSelection(id: string): void {
    setSelection((liste) =>
      liste.includes(id) ? liste.filter((autre) => autre !== id) : [...liste, id],
    );
  }

  function basculerRecherche(ligne: LigneSansContrat): void {
    definirRecherche.mutate(
      { apprenantId: ligne.apprenantId, active: !ligne.rechercheActive },
      {
        onSuccess: (reponse) => toast(`${ligne.apprenant} · ${reponse.message}`),
        onError: (erreur) =>
          toast(erreur instanceof ErreurApi ? erreur.message : 'La recherche n’a pas été modifiée.'),
      },
    );
  }

  const valeur = (nombre: number | undefined): string =>
    nombre === undefined ? '—' : String(nombre);

  const actionsLigne = {
    vuePlacement,
    mobilites: data?.filtres.mobilites ?? [],
    enCours: definirMobilite.isPending || definirRecherche.isPending,
    onMobilite: (ligne: LigneSansContrat, valeurMobilite: Mobilite) =>
      definirMobilite.mutate({ apprenantId: ligne.apprenantId, mobilite: valeurMobilite }),
    onRecherche: basculerRecherche,
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Apprenants sans entreprise"
        sousTitre="Pôle pédagogique · inscrits en formation, en recherche active, vue filtrable pour cibler la diffusion d’offres"
        actions={
          <BoutonMint onClick={() => setDiffusionOuverte(true)} disabled={selection.length === 0}>
            {selection.length === 0
              ? 'Diffuser les offres à la sélection'
              : `Diffuser les offres à la sélection (${selection.length})`}
          </BoutonMint>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Sans entreprise"
          valeur={valeur(compteurs?.sansEntreprise)}
          detail="inscrits en formation"
          ton="red"
        />
        <Compteur
          libelle="En recherche active"
          valeur={valeur(compteurs?.enRechercheActive)}
          detail={
            compteurs && compteurs.rechercheNonDeclaree > 0
              ? `${compteurs.rechercheNonDeclaree} sans recherche déclarée`
              : 'déclarée au dossier'
          }
          ton="warm"
        />
        <Compteur
          libelle="Immersions planifiées"
          valeur={valeur(compteurs?.immersions)}
          detail="prévues, en cours ou à trancher"
          ton="blue"
        />
        <Compteur
          libelle="Placés ce mois"
          valeur={valeur(compteurs?.placesCeMois)}
          detail="oui d’une entreprise"
          ton="mint"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-card sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
        <Filtre libelle="Recherche">
          <ChampRecherche
            valeur={recherche}
            onChange={setRecherche}
            etiquette="Rechercher un apprenant"
            placeholder={vuePlacement ? 'Nom ou prénom' : 'Nom, prénom ou e-mail'}
          />
        </Filtre>

        <Filtre libelle="Statut de recherche" id="filtre-etat">
          <select
            id="filtre-etat"
            value={etat}
            onChange={(evenement) => setEtat(evenement.target.value as EtatRecherche)}
            className={CLASSE_FILTRE}
          >
            {(data?.filtres.etats ?? ETATS_PAR_DEFAUT).map((entree) => (
              <option key={entree.valeur} value={entree.valeur}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </Filtre>

        {!vuePlacement && (
          <>
            <Filtre libelle="Sexe" id="filtre-sexe">
              <select
                id="filtre-sexe"
                value={sexe}
                onChange={(evenement) => setSexe(evenement.target.value as 'F' | 'M' | '')}
                className={CLASSE_FILTRE}
              >
                <option value="">Tous</option>
                <option value="F">Femme</option>
                <option value="M">Homme</option>
              </select>
            </Filtre>

            <Filtre libelle="Âge" id="filtre-age">
              <select
                id="filtre-age"
                value={trancheAge}
                onChange={(evenement) =>
                  setTrancheAge(evenement.target.value as CodeTrancheAge | '')
                }
                className={CLASSE_FILTRE}
              >
                <option value="">Tous</option>
                {(data?.filtres.tranchesAge ?? []).map((tranche) => (
                  <option key={tranche.code} value={tranche.code}>
                    {tranche.libelle}
                  </option>
                ))}
              </select>
            </Filtre>

            <Filtre libelle="Mineur" id="filtre-mineur">
              <select
                id="filtre-mineur"
                value={mineur ? 'oui' : ''}
                onChange={(evenement) => setMineur(evenement.target.value === 'oui')}
                className={CLASSE_FILTRE}
              >
                <option value="">Tous</option>
                <option value="oui">Mineurs uniquement</option>
              </select>
            </Filtre>

            <Filtre libelle="Situation de handicap" id="filtre-rqth">
              <select
                id="filtre-rqth"
                value={rqth ? 'oui' : ''}
                onChange={(evenement) => setRqth(evenement.target.value === 'oui')}
                className={CLASSE_FILTRE}
              >
                <option value="">Tous</option>
                <option value="oui">RQTH uniquement</option>
              </select>
            </Filtre>
          </>
        )}

        <Filtre libelle="Formation" id="filtre-formation">
          <select
            id="filtre-formation"
            value={formation}
            onChange={(evenement) => setFormation(evenement.target.value)}
            className={CLASSE_FILTRE}
          >
            <option value="">Toutes</option>
            {(data?.filtres.formations ?? []).map((entree) => (
              <option key={entree.code} value={entree.code}>
                {entree.titre} ({entree.code})
              </option>
            ))}
          </select>
        </Filtre>

        {!vuePlacement && (
          <>
            <Filtre libelle="Commune" id="filtre-commune">
              <select
                id="filtre-commune"
                value={commune}
                onChange={(evenement) => setCommune(evenement.target.value)}
                className={CLASSE_FILTRE}
              >
                <option value="">Toutes</option>
                {(data?.filtres.communes ?? []).map((entree) => (
                  <option key={entree} value={entree}>
                    {entree}
                  </option>
                ))}
              </select>
            </Filtre>

            <Filtre libelle="Mobilité" id="filtre-mobilite">
              <select
                id="filtre-mobilite"
                value={mobilite}
                onChange={(evenement) => setMobilite(evenement.target.value as Mobilite | '')}
                className={CLASSE_FILTRE}
              >
                <option value="">Toutes</option>
                {(data?.filtres.mobilites ?? []).map((entree) => (
                  <option key={entree.valeur} value={entree.valeur}>
                    {entree.libelle}
                  </option>
                ))}
              </select>
            </Filtre>
          </>
        )}

        <Filtre libelle="Situation" id="filtre-situation">
          <select
            id="filtre-situation"
            value={situation}
            onChange={(evenement) =>
              setSituation(evenement.target.value as SituationRecherche | '')
            }
            className={CLASSE_FILTRE}
          >
            <option value="">Toutes</option>
            {(data?.filtres.situations ?? []).map((entree) => (
              <option key={entree.valeur} value={entree.valeur}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </Filtre>

        <Filtre libelle="En recherche depuis" id="filtre-anciennete">
          <select
            id="filtre-anciennete"
            value={anciennete}
            onChange={(evenement) => setAnciennete(evenement.target.value as CodeAnciennete | '')}
            className={CLASSE_FILTRE}
          >
            <option value="">Toutes durées</option>
            {(data?.filtres.anciennetes ?? []).map((entree) => (
              <option key={entree.code} value={entree.code}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </Filtre>

        <Filtre libelle="Offres reçues" id="filtre-offres">
          <select
            id="filtre-offres"
            value={offres}
            onChange={(evenement) => setOffres(evenement.target.value as FiltreOffres | '')}
            className={CLASSE_FILTRE}
          >
            <option value="">Indifférent</option>
            {(data?.filtres.offres ?? []).map((entree) => (
              <option key={entree.valeur} value={entree.valeur}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </Filtre>

        <div className="flex items-end">
          <BoutonSecondaire onClick={reinitialiser}>Réinitialiser</BoutonSecondaire>
        </div>
      </div>

      <Carte
        titre="Liste filtrable"
        actions={
          <>
            {aLeMatching && (
              <BoutonSecondaire onClick={() => navigate('/com-matching')}>
                Ouvrir le matching
              </BoutonSecondaire>
            )}
            {!estCommercial && (
              <BoutonSecondaire
                onClick={() => setTransmissionOuverte(true)}
                disabled={selection.length === 0}
              >
                Transmettre au commercial
              </BoutonSecondaire>
            )}
          </>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi ? error.message : 'La liste n’a pas pu être chargée.'
              }
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement des apprenants…" />}

        {!isLoading && !error && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-2 sm:px-6">
              <label className="flex min-h-[40px] items-center gap-2 text-sm text-codialis-navy">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded"
                  checked={tousSelectionnes}
                  disabled={total === 0}
                  onChange={() =>
                    setSelection(tousSelectionnes ? [] : lignes.map((ligne) => ligne.apprenantId))
                  }
                />
                Tout sélectionner ({total})
              </label>
              {selection.length > 0 && (
                <span className="text-xs text-codialis-gray">
                  {selection.length} sélectionné{selection.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {total === 0 ? (
              <ListeVide
                etat={etat}
                nonDeclarees={compteurs?.rechercheNonDeclaree ?? 0}
                onVoirNonDeclarees={() => setEtat('NON_DECLAREE')}
              />
            ) : (
              <>
                {/* Sous l'écran large, une carte par personne : le tableau
                    défilait de côté sur la tablette. */}
                <ul className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:hidden">
                  {lignesAffichees.map((ligne) => (
                    <CartePersonne
                      key={ligne.apprenantId}
                      ligne={ligne}
                      selectionne={selection.includes(ligne.apprenantId)}
                      onSelectionner={() => basculerSelection(ligne.apprenantId)}
                      {...actionsLigne}
                    />
                  ))}
                </ul>

                <div className="relative hidden overflow-x-auto xl:block">
                  <table className="w-full min-w-[960px] text-sm">
                    <thead className="border-b border-gray-100 bg-gray-50">
                      <tr>
                        <th className="w-10 px-4 py-3">
                          <span className="sr-only">Sélection</span>
                        </th>
                        {(vuePlacement ? COLONNES_PLACEMENT : COLONNES).map((colonne) => (
                          <th
                            key={colonne}
                            className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                          >
                            {colonne}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lignesAffichees.map((ligne) => (
                        <LignePersonne
                          key={ligne.apprenantId}
                          ligne={ligne}
                          selectionne={selection.includes(ligne.apprenantId)}
                          onSelectionner={() => basculerSelection(ligne.apprenantId)}
                          {...actionsLigne}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={page}
                  pages={pages}
                  total={total}
                  affiches={lignesAffichees.length}
                  taillePage={taillePage}
                  onPage={setPage}
                  onChangerTaille={setTaillePage}
                  uniteLabel="apprenant"
                />
              </>
            )}
          </>
        )}

        <div className="border-t border-gray-100 p-4 text-[11px] leading-relaxed text-codialis-gray sm:p-6">
          Figurent ici les dossiers CFA retenus à l’entretien, sans contrat en vigueur ni « oui »
          d’entreprise en cours ; un dossier OF ne passe pas par l’entreprise. La recherche est
          déclarée au dossier : posée à la retenue du candidat, à la rupture avec maintien en
          formation et à l’immersion non validée, elle se suspend ici pour qui ne cherche plus.
          Le nombre d’entreprises suggérées est celui du matching, à son dernier calcul.
        </div>
      </Carte>

      <ModaleDiffusionOffres
        apprenantIds={selection}
        ouverte={diffusionOuverte}
        onFermer={() => setDiffusionOuverte(false)}
        onDiffuse={(resultat) => {
          setDiffusionOuverte(false);
          setSelection([]);
          toast(
            resultat.ecartes.length > 0
              ? `${resultat.message} ${resultat.ecartes.length} personne${resultat.ecartes.length > 1 ? 's' : ''} écartée${resultat.ecartes.length > 1 ? 's' : ''}.`
              : resultat.message,
          );
        }}
      />
      <ModaleTransmission
        apprenantIds={selection}
        ouverte={transmissionOuverte}
        onFermer={() => setTransmissionOuverte(false)}
        onTransmis={(resultat) => {
          setTransmissionOuverte(false);
          setSelection([]);
          toast(resultat.message);
        }}
      />
    </div>
  );
}

const CLASSE_FILTRE = 'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm';

const ETATS_PAR_DEFAUT: { valeur: EtatRecherche; libelle: string }[] = [
  { valeur: 'ACTIVE', libelle: 'En recherche active' },
];

const COLONNES = [
  'Apprenant',
  'Formation · site',
  'Mobilité',
  'Recherche',
  'Situation',
  'Offres et matching',
];

const COLONNES_PLACEMENT = ['Apprenant', 'Formation · site', 'Recherche', 'Situation', 'Offres et matching'];

const CLASSE_LIBELLE = 'mb-1 block text-[10px] font-bold uppercase tracking-wider text-codialis-gray';

/** Sans `id`, le champ porte sa propre étiquette (la recherche) : le libellé n'est que visuel. */
function Filtre({
  libelle,
  id,
  children,
}: {
  libelle: string;
  id?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      {id ? (
        <label htmlFor={id} className={CLASSE_LIBELLE}>
          {libelle}
        </label>
      ) : (
        <span aria-hidden="true" className={CLASSE_LIBELLE}>
          {libelle}
        </span>
      )}
      {children}
    </div>
  );
}

function ListeVide({
  etat,
  nonDeclarees,
  onVoirNonDeclarees,
}: {
  etat: EtatRecherche;
  nonDeclarees: number;
  onVoirNonDeclarees: () => void;
}): JSX.Element {
  return (
    <div className="space-y-3 px-4 py-10 text-center text-sm text-codialis-gray sm:px-6">
      <p>Aucun apprenant ne correspond à ces critères.</p>
      {etat === 'ACTIVE' && nonDeclarees > 0 && (
        <p>
          {nonDeclarees} personne{nonDeclarees > 1 ? 's' : ''} sans entreprise n’
          {nonDeclarees > 1 ? 'ont' : 'a'} pas de recherche déclarée.{' '}
          <button
            type="button"
            onClick={onVoirNonDeclarees}
            className="font-semibold text-codialis-sky hover:underline"
          >
            Les afficher
          </button>
        </p>
      )}
    </div>
  );
}

interface ProprietesPersonne {
  ligne: LigneSansContrat;
  vuePlacement: boolean;
  mobilites: { valeur: Mobilite; libelle: string }[];
  selectionne: boolean;
  enCours: boolean;
  onSelectionner: () => void;
  onMobilite: (ligne: LigneSansContrat, mobilite: Mobilite) => void;
  onRecherche: (ligne: LigneSansContrat) => void;
}

function Identite({ ligne, vuePlacement }: { ligne: LigneSansContrat; vuePlacement: boolean }): JSX.Element {
  const details = vuePlacement
    ? []
    : [
        ligne.age == null ? null : `${ligne.age} ans`,
        ligne.sexe === 'F' ? 'Femme' : ligne.sexe === 'M' ? 'Homme' : null,
        ligne.ville ?? null,
      ].filter((morceau): morceau is string => Boolean(morceau));
  return (
    <div className="min-w-0">
      <Link
        to={`/adm-dossier/${ligne.apprenantId}`}
        className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:items-center"
      >
        {ligne.apprenant}
      </Link>
      {details.length > 0 && (
        <div className="text-[11px] text-codialis-gray">{details.join(' · ')}</div>
      )}
      {(ligne.mineur || ligne.rqth || !ligne.joignable) && (
        <div className="mt-1 flex flex-wrap gap-1">
          {ligne.mineur && <Pastille texte="Mineur" ton="warning" />}
          {ligne.rqth && <Pastille texte="RQTH" ton="info" />}
          {!ligne.joignable && <Pastille texte="Sans e-mail ni espace" ton="danger" />}
        </div>
      )}
    </div>
  );
}

function FormationSite({ ligne }: { ligne: LigneSansContrat }): JSX.Element {
  return (
    <div className="min-w-0 text-xs text-codialis-gray">
      <div className="break-words">{ligne.formation ?? 'Formation non précisée'}</div>
      <div>Antenne {ligne.siteCode}</div>
    </div>
  );
}

function ChoixMobilite({
  ligne,
  mobilites,
  enCours,
  onMobilite,
}: Pick<ProprietesPersonne, 'ligne' | 'mobilites' | 'enCours' | 'onMobilite'>): JSX.Element {
  return (
    <select
      aria-label={`Mobilité de ${ligne.apprenant}`}
      value={ligne.mobilite ?? ''}
      onChange={(evenement) => onMobilite(ligne, evenement.target.value as Mobilite)}
      disabled={enCours}
      className="w-full min-w-[160px] max-w-[220px] rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs"
    >
      <option value="" disabled>
        Non déclarée
      </option>
      {mobilites.map((entree) => (
        <option key={entree.valeur} value={entree.valeur}>
          {entree.libelle}
        </option>
      ))}
    </select>
  );
}

/** La recherche se déclare ou se suspend d'un geste : c'est elle qui ouvre la diffusion. */
function Recherche({
  ligne,
  enCours,
  onRecherche,
}: Pick<ProprietesPersonne, 'ligne' | 'enCours' | 'onRecherche'>): JSX.Element {
  return (
    <div className="space-y-1">
      <div className="text-xs text-codialis-gray">
        {ligne.rechercheActive ? 'Depuis' : 'Sans entreprise depuis'} {ligne.rechercheDepuis}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={ligne.rechercheActive}
        aria-label={`Recherche active de ${ligne.apprenant}`}
        disabled={enCours}
        onClick={() => onRecherche(ligne)}
        className="inline-flex min-h-[32px] items-center gap-2 whitespace-nowrap rounded-lg text-xs font-semibold text-codialis-navy disabled:opacity-60 [@media(pointer:coarse)]:min-h-[40px]"
      >
        <span
          aria-hidden="true"
          className={`relative inline-block h-5 w-9 shrink-0 rounded-full transition ${
            ligne.rechercheActive ? 'bg-codialis-mint' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition ${
              ligne.rechercheActive ? 'left-[18px]' : 'left-0.5'
            }`}
          />
        </span>
        {ligne.rechercheActive ? 'Recherche active' : 'Non déclarée'}
      </button>
    </div>
  );
}

function OffresEtMatching({ ligne }: { ligne: LigneSansContrat }): JSX.Element {
  return (
    <div className="space-y-1 text-xs text-codialis-gray">
      <div className="flex items-start gap-1.5">
        <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          {ligne.offresRecues === 0
            ? 'Aucune offre reçue'
            : `${ligne.offresRecues} offre${ligne.offresRecues > 1 ? 's' : ''} reçue${ligne.offresRecues > 1 ? 's' : ''} · dernière le ${dateCourte(ligne.derniereOffreLe)}`}
        </span>
      </div>
      <div className="flex items-start gap-1.5">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          {ligne.suggestionsMatching === 0
            ? 'Aucune entreprise suggérée'
            : `${ligne.suggestionsMatching} entreprise${ligne.suggestionsMatching > 1 ? 's' : ''} suggérée${ligne.suggestionsMatching > 1 ? 's' : ''}${ligne.meilleurScore !== null ? ` · ${ligne.meilleurScore} %` : ''}`}
        </span>
      </div>
    </div>
  );
}

/** La case porte une zone de 40 px : au doigt, la case seule faisait 16 px. */
function CaseSelection({
  ligne,
  selectionne,
  onSelectionner,
}: Pick<ProprietesPersonne, 'ligne' | 'selectionne' | 'onSelectionner'>): JSX.Element {
  return (
    <label className="-m-2 flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        aria-label={`Sélectionner ${ligne.apprenant}`}
        className="h-4 w-4 rounded"
        checked={selectionne}
        onChange={onSelectionner}
      />
    </label>
  );
}

function CartePersonne(props: ProprietesPersonne): JSX.Element {
  const { ligne, vuePlacement, selectionne, onSelectionner } = props;
  return (
    <li
      className={`min-w-0 space-y-3 rounded-xl border p-4 ${
        selectionne ? 'border-codialis-mint bg-codialis-mint/5' : 'border-gray-100'
      }`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <CaseSelection ligne={ligne} selectionne={selectionne} onSelectionner={onSelectionner} />
        <div className="min-w-0 flex-1">
          <Identite ligne={ligne} vuePlacement={vuePlacement} />
        </div>
        <Pastille texte={ligne.situationLibelle} ton={ligne.situationTon} />
      </div>
      <FormationSite ligne={ligne} />
      {!vuePlacement && (
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-codialis-gray">
            Mobilité
          </div>
          <ChoixMobilite {...props} />
        </div>
      )}
      <Recherche {...props} />
      <OffresEtMatching ligne={ligne} />
    </li>
  );
}

function LignePersonne(props: ProprietesPersonne): JSX.Element {
  const { ligne, vuePlacement, selectionne, onSelectionner } = props;
  return (
    <tr className={`align-top transition hover:bg-gray-50 ${selectionne ? 'bg-codialis-mint/5' : ''}`}>
      <td className="px-2 py-1">
        <CaseSelection ligne={ligne} selectionne={selectionne} onSelectionner={onSelectionner} />
      </td>
      <td className="min-w-[180px] px-3 py-3">
        <Identite ligne={ligne} vuePlacement={vuePlacement} />
      </td>
      <td className="min-w-[140px] max-w-[240px] px-3 py-3">
        <FormationSite ligne={ligne} />
      </td>
      {!vuePlacement && (
        <td className="px-3 py-3">
          <ChoixMobilite {...props} />
        </td>
      )}
      <td className="px-3 py-3">
        <Recherche {...props} />
      </td>
      <td className="px-3 py-3">
        <Pastille texte={ligne.situationLibelle} ton={ligne.situationTon} />
        {ligne.situation === 'IMMERSION_A_TRANCHER' && (
          <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Réponse de l’entreprise attendue
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        <OffresEtMatching ligne={ligne} />
      </td>
    </tr>
  );
}
